import { store } from '@/common/root';
import Model from '@/common/models/common.model';
import ROLES from '@/common/constants/roles.constants';
import {
  NODES_STATUS_MAP,
  NODES_STATUSES_V3
} from '@/common/constants/nodes.constants';
import { NODE_SERVICE_TAG_TYPE } from '@/common/constants/services.constants';

interface NodeNetworkInterfaceDetailSpeed {
  current: number,
  max: number
}

interface NodeNetworkInterfaceDetailNetworkEncryption {
  status: string,
  subnets: string[]
}

interface NodeNetworkInterfaceDetailNetwork {
  encryption: NodeNetworkInterfaceDetailNetworkEncryption,
  id: string,
  inboundAllowList: string[],
  inboundAllowListString: string,
  inboundDenyList: string[],
  inboundDenyListString: string,
  name: string,
  roles: string[],
  vlan: number
}

interface NodeNetworkInterfaceDetail {
  built_on: string,
  name: string,
  ipv4: string[],
  ipv6?: string[],
  gw4: string,
  gw6?: string,
  dhcp4_enabled: boolean,
  dhcp6_enabled: boolean,
  mtu: number,
  roles_set: string,
  ignore_auto_routes_v4: boolean,
  ignore_auto_routes_v6: boolean,
  ifaces: string[],
  connected_mode: boolean,
  bond_type: string,
  mac_addr: string,
  node_id: string,
  multicast: boolean,
  type: string,
  dhcp4: string,
  dhcp6: string,
  state: string,
  speeds: NodeNetworkInterfaceDetailSpeed,
  duplex: boolean,
  plugged: boolean,
  rx_bytes: number,
  rx_dropped: number,
  rx_errors: number,
  rx_overruns: number,
  rx_packets: number,
  tx_bytes: number,
  tx_dropped: number,
  tx_errors: number,
  tx_overruns: number,
  tx_packets: number,
  existErrors: boolean,
  firstIpAddress: string,
  ipv4Addresses: string[],
  ipv4AddressesWithoutLinkLocal: string[],
  ipv6Addresses: string[],
  ipv6AddressesWithoutLinkLocal: string[],
  isHalfDuplex: boolean,
  isSpeedSlow: boolean,
  statusIcon: string,
  network: NodeNetworkInterfaceDetailNetwork
}

export interface NodeService {
  key: string,
  state: string,
  title: string,
  type: string
}

interface NodefcPort {
  port: string
}
export default class Node extends Model {
  get statusText(): string {
    return NODES_STATUS_MAP[this.status].text;
  }

  get statusIcon(): string {
    return NODES_STATUS_MAP[this.status].icon;
  }

  get tagTypeMap() {
    return NODE_SERVICE_TAG_TYPE;
  }

  findIfaceWithRole(role): NodeNetworkInterfaceDetail | undefined {
    return this.networks.find((iface) => {
      const network = store.getters['rolesets/getNetworkById'](iface.roles_set);
      return network && Array.isArray(network.roles) && network.roles.indexOf(role) !== -1;
    });
  }

  get name(): string {
    return this.attributes.name;
  }

  get host(): string {
    return this.attributes.host;
  }

  get ip(): string {
    return this.attributes.managementIp;
  }

  /**
   * Wheter node is assigned to storage cluster
   * @returns {boolean}
   */
  get isAssigned(): boolean {
    return this.attributes.isAssigned;
  }

  get isOnline(): boolean {
    return this.attributes.isOnline;
  }

  get isOffline(): boolean {
    return this.attributes.isOffline;
  }

  get isHealthy(): boolean {
    return this.attributes.isHealthy;
  }

  get isComputeManagement(): boolean {
    const networkInterfaces = this.networks;

    if (!networkInterfaces?.length) {
      return false;
    }

    let computePublicExists = false;
    let computePrivateExists = false;
    let computeApiExists = false;
    let computeBackupExists = false;

    networkInterfaces.forEach((networkInterface) => {
      const currNetwork = store.getters['rolesets/getNetworkById'](networkInterface.roles_set);
      if (!currNetwork) return;

      const roles = currNetwork.roles || [];
      if (roles.includes(ROLES.COMPUTE_PRIVATE)) {
        computePrivateExists = true;
      }
      if (roles.includes(ROLES.COMPUTE_API)) {
        computeApiExists = true;
      }
      if (roles.includes(ROLES.COMPUTE_PUBLIC)) {
        computePublicExists = true;
      }
      if (roles.includes(ROLES.NBD)) {
        computeBackupExists = true;
      }
    });

    return computePrivateExists && computeApiExists && computePublicExists && computeBackupExists;
  }

  get isInHA(): boolean {
    return this.attributes.isInHa;
  }

  get networks(): NodeNetworkInterfaceDetail[] {
    return store.getters['nodesInterfaces/getInterfaces'](this.id);
  }

  get isFailed(): boolean {
    return this.attributes.isFailed;
  }

  get isAvailable(): boolean {
    return this.attributes.isAvailable;
  }

  get isOperational(): boolean {
    return this.attributes.isOperational;
  }

  get services(): NodeService {
    return this.attributes.services;
  }

  get ipAddressesList(): string[] {
    return this.attributes.ipAddressesList;
  }

  get ipV4Addresses(): string[] {
    return this.attributes.ipV4Addresses;
  }

  get ipV6Addresses(): string[] {
    return this.attributes.ipV6Addresses;
  }

  get status(): string {
    return this.attributes.status;
  }

  get isManagement(): boolean {
    return this.attributes.isPrimary || this.attributes.isInHa;
  }

  get isPrimary(): boolean {
    return this.attributes.isPrimary;
  }

  get isAllowS3(): boolean {
    return !!this.findIfaceWithRole(ROLES.S3_PUBLIC) && !!this.findIfaceWithRole(ROLES.OSTOR_PRIVATE);
  }

  get isAllowStorage(): boolean {
    return !!this.findIfaceWithRole(ROLES.STORAGE);
  }

  get isInS3(): boolean {
    return this.attributes.isInS3;
  }

  get isInNFS(): boolean {
    return this.attributes.isInNFS;
  }

  get isInFileStorage(): boolean {
    return this.attributes.isInFileStorage;
  }

  get isInABGW(): boolean {
    return this.attributes.isInABGW;
  }

  get isInCompute(): boolean {
    return this.attributes.isInCompute;
  }

  get isComputeController(): boolean {
    return this.attributes.isComputeController;
  }

  get isComputeWorker(): boolean {
    return this.attributes.isComputeWorker;
  }

  get isInRecovery(): boolean {
    return this.attributes.isInRecovery;
  }

  get hasRoleAdminPanel(): boolean {
    return !!this.findIfaceWithRole(ROLES.MANAGEMENT_PANEL);
  }

  get isInStorage(): boolean {
    return this.attributes.isInStorage;
  }

  get isInProgress(): boolean {
    return this.status === NODES_STATUSES_V3.IN_PROGRESS;
  }

  get isAllowCompute(): boolean {
    return this.isComputeManagement;
  }

  get isAllowBlockStorage(): boolean {
    return !!this.findIfaceWithRole(ROLES.ISCSI);
  }

  get isAllowAbgwStorage(): boolean {
    return !!this.findIfaceWithRole(ROLES.ABGW_PUBLIC) && !!this.findIfaceWithRole(ROLES.ABGW_PRIVATE);
  }

  get fcPorts(): NodefcPort[] {
    return store.getters['nodesFiberChannel/getFiberChannel'](this.id);
  }

  get isMaintenance(): boolean {
    return this.attributes.isMaintenance;
  }

  get roles(): object {
    return this.attributes.roles || {};
  }

  get rolesArray(): string[] {
    return Object.keys(this.roles) || [];
  }
}
